import { useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { ITankInfo, TankShape } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { InfoGroupCard, type InfoGroupEntry } from '@/components/infoGroupCard/InfoGroupCard.tsx';
import {
  DeviceInfoOverview,
  deviceInfoOverviewGridColumns,
} from '@/components/informationModal/DeviceInfoOverview.tsx';
import type { DeviceSummary } from '@/types';
import { responsive } from '@/utils';

import { useTankShape } from './tankInfoFields/useTankShape.tsx';

type InfoFieldsProps = {
  tankInfo: ITankInfo;
};

export const TankInfoFields = ({ tankInfo }: InfoFieldsProps) => {
  const { t } = useTranslation(['common', 'bargeControl', 'deviceConfig']);

  const {
    tankIndex,
    tankShape,
    orientation,
    dimensions,
    density,
    content,
    alarmLevels,
    sensorRange,
    level,
    tankUnit,
  } = useTankShape(tankInfo);

  const overviewExtraEntries = useMemo(
    (): InfoGroupEntry[] => [
      { id: 'tankIndex', label: t('bargeControl:tank.index'), value: tankIndex },
    ],
    [t, tankIndex]
  );

  const measurementEntries = useMemo(
    (): InfoGroupEntry[] => [
      {
        id: 'level',
        label: t('bargeControl:tank.level'),
        value: level?.height,
        unit: 'm',
        withSpaceToUnit: true,
      },
      {
        id: 'volume',
        label: t('bargeControl:tank.volume'),
        value: level?.volume,
        unit: 'm\u00B3',
        withSpaceToUnit: true,
      },
      {
        id: 'density',
        label: t('bargeControl:density'),
        value: density,
        unit: 'kg/m\u00B3',
        withSpaceToUnit: true,
      },
      { id: 'content', label: t('bargeControl:tank.content.name'), value: content },
    ],
    [content, density, level?.height, level?.volume, t]
  );

  const shapeEntries = useMemo((): InfoGroupEntry[] => {
    const list: InfoGroupEntry[] = [
      { id: 'shape', label: t('bargeControl:tank.shape'), value: tankShape },
    ];
    if (dimensions) {
      if (tankShape === TankShape.Cylindrical) {
        list.push({
          id: 'dimensions',
          label: t('bargeControl:tank.dimensionsCylindrical'),
          value: `${dimensions.tankDiameter} m / ${dimensions.tankHeight} m`,
        });
      } else {
        list.push({
          id: 'dimensions',
          label: t('bargeControl:tank.dimensionsRectangular'),
          value: `${dimensions.tankLength} m / ${dimensions.tankWidth} m / ${dimensions.tankHeight} m`,
        });
      }
    }
    list.push(
      { id: 'orientation', label: t('bargeControl:tank.orientation'), value: orientation },
      { id: 'tankUnit', label: t('bargeControl:tank.unit'), value: tankUnit }
    );
    return list;
  }, [dimensions, orientation, t, tankShape, tankUnit]);

  const rangeEntries = useMemo(
    (): InfoGroupEntry[] => [
      {
        id: 'alarmLevels',
        label: t('bargeControl:configModal.alarmLevels'),
        value: alarmLevels != null ? `${alarmLevels.low} m / ${alarmLevels.high} m` : undefined,
      },
      {
        id: 'sensorRange',
        label: t('bargeControl:configModal.sensorLevels'),
        value: sensorRange != null ? `${sensorRange.min} / ${sensorRange.max}` : undefined,
      },
    ],
    [alarmLevels, sensorRange, t]
  );

  return (
    <FlexAligner direction='column' gap={responsive.spacing['3xlarge']}>
      <DeviceInfoOverview device={tankInfo as DeviceSummary} extraEntries={overviewExtraEntries} />
      <InfoGroupCard entries={measurementEntries} columns={deviceInfoOverviewGridColumns} />
      <InfoGroupCard entries={shapeEntries} columns={deviceInfoOverviewGridColumns} />
      <InfoGroupCard entries={rangeEntries} columns={deviceInfoOverviewGridColumns} />
    </FlexAligner>
  );
};
